'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MapRegion {
  id: string;
  name: string;
  coordinates: string;
  color: string;
}

interface InteractiveMapProps {
  regions: MapRegion[];
  selectedRegion: string | null;
  onRegionSelect: (regionId: string) => void;
}

const InteractiveMap = ({
  regions,
  selectedRegion,
  onRegionSelect,
}: InteractiveMapProps) => {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);

  return (
    <div className="relative bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="mb-4">
        <h3 className="font-headline font-bold text-xl text-foreground mb-2">
          Interactive Coverage Map
        </h3>
        <p className="font-body text-sm text-muted-foreground">
          Click on a region to view detailed logistics information
        </p>
      </div>

      <div className="relative aspect-[16/10] bg-muted rounded-lg overflow-hidden">
        <svg
          viewBox="0 0 800 500"
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFD700" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#DAA520" stopOpacity="0.2" />
            </linearGradient>
          </defs>

          <rect width="800" height="500" fill="url(#mapGradient)" />

          {regions.map((region) => {
            const isSelected = selectedRegion === region.id;
            const isHovered = hoveredRegion === region.id;

            return (
              <g key={region.id}>
                <path
                  d={region.coordinates}
                  fill={
                    isSelected
                      ? '#FFD700'
                      : isHovered
                      ? '#FFA500'
                      : region.color
                  }
                  stroke="#1B365D"
                  strokeWidth="2"
                  className="cursor-pointer transition-all duration-300"
                  opacity={isSelected ? 0.9 : isHovered ? 0.7 : 0.5}
                  onClick={() => onRegionSelect(region.id)}
                  onMouseEnter={() => setHoveredRegion(region.id)}
                  onMouseLeave={() => setHoveredRegion(null)}
                />
              </g>
            );
          })}

          <circle cx="400" cy="150" r="8" fill="#DC2626" opacity="0.8" />
          <circle cx="350" cy="200" r="8" fill="#DC2626" opacity="0.8" />
          <circle cx="450" cy="180" r="8" fill="#DC2626" opacity="0.8" />
          <circle cx="380" cy="250" r="8" fill="#DC2626" opacity="0.8" />
          <circle cx="420" cy="280" r="8" fill="#DC2626" opacity="0.8" />
        </svg>

        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-elevation-2">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-destructive" />
              <span className="text-xs font-body text-foreground">
                Major Hubs
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-golden-mid" />
              <span className="text-xs font-body text-foreground">
                Active Region
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {regions.map((region) => (
          <button
            key={region.id}
            onClick={() => onRegionSelect(region.id)}
            className={`p-3 rounded-lg border transition-all duration-300 ${
              selectedRegion === region.id
                ? 'border-golden-mid bg-golden-light/10' :'border-border hover:border-golden-mid/50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Icon
                name="MapIcon"
                size={16}
                className={
                  selectedRegion === region.id
                    ? 'text-golden-mid' :'text-muted-foreground'
                }
              />
              <span
                className={`text-sm font-body ${
                  selectedRegion === region.id
                    ? 'text-foreground font-semibold'
                    : 'text-muted-foreground'
                }`}
              >
                {region.name}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default InteractiveMap;